import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Headphones,
  MailCheck,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  UserCheck,
  WalletCards,
} from 'lucide-react';
import logo from '../assets/westcounty-mark.png';
import { API_BASE_URL } from '../config/api.js';
import { findTopupProduct, formatRupiah, topupCategories } from '../config/topupProducts.js';

const initialForm = {
  categoryId: '',
  productId: '',
  ucpName: '',
  characterName: '',
  levelQuantity: '1',
  promoCode: '',
  assetDetail: '',
  email: '',
};

function cleanName(value) {
  return value.replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '');
}

function pickCharacterLabel(character) {
  if (!character) return '-';
  return `${character.username} — ${pickCharacterMeta(character)}`;
}

function pickCharacterMeta(character) {
  if (!character) return '';
  return `Level ${character.level}`;
}

function CustomTopupDropdown({ value, options, placeholder, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((item) => item.value === value) || null;

  const closeAndChoose = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div className={open ? 'custom-origin-select topup-custom-select is-open' : 'custom-origin-select topup-custom-select'}>
      <button
        type="button"
        className="custom-origin-trigger topup-custom-trigger"
        onClick={() => !disabled && setOpen((current) => !current)}
        aria-expanded={open}
        disabled={disabled}
      >
        <span>{selected?.label || placeholder}</span>
        <ChevronDown size={17} />
      </button>
      {open && !disabled ? (
        <div className="custom-origin-menu topup-custom-menu">
          {options.length === 0 ? (
            <button type="button" disabled>Tidak ada pilihan</button>
          ) : options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={option.value === value ? 'is-selected' : ''}
              onClick={() => closeAndChoose(option.value)}
            >
              <span>{option.label}</span>
              {option.meta ? <small>{option.meta}</small> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function TopupPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [accountCheck, setAccountCheck] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [ucpValidated, setUcpValidated] = useState(false);
  const [message, setMessage] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoCheck, setPromoCheck] = useState(null);
  const [promoStatus, setPromoStatus] = useState({ loading: true, activeCount: 0, enabled: false, message: '' });
  const [returnStatus, setReturnStatus] = useState(null);

  const { category, product } = useMemo(
    () => findTopupProduct(form.categoryId, form.productId),
    [form.categoryId, form.productId]
  );

  const products = category?.products || [];
  const isLevelCategory = form.categoryId === 'character_level';
  const isLevelQuantityProduct = isLevelCategory && product?.allowQuantity;
  const levelQuantity = Math.max(1, parseInt(form.levelQuantity || '1', 10) || 1);
  const baseTotal = product ? (isLevelQuantityProduct ? product.price * levelQuantity : product.price) : 0;
  const promoCodeClean = form.promoCode.trim().toUpperCase();
  const promoFeatureEnabled = !promoStatus.loading && promoStatus.enabled && Number(promoStatus.activeCount || 0) > 0;
  const promoApplies = promoCheck?.type === 'success' && promoCheck.code === promoCodeClean && Number(promoCheck.baseAmount || 0) === Number(baseTotal || 0);
  const discount = promoApplies ? Number(promoCheck.discount || 0) : 0;
  const total = Math.max(0, baseTotal - discount);
  const selectedCharacter = characters.find((item) => item.username === form.characterName);
  const pageParams = new URLSearchParams(window.location.search);
  const fromDashboard = pageParams.get('from') === 'dashboard';
  const backUrl = fromDashboard ? '/account/dashboard' : '/';
  const backLabel = fromDashboard ? 'Kembali Dashboard' : 'Beranda';

  const categoryOptions = topupCategories.map((item) => ({
    value: item.id,
    label: item.label,
    meta: item.hint,
  }));

  const productOptions = products.map((item) => ({
    value: item.id,
    label: `${item.name} — ${formatRupiah(item.price)}`,
    meta: item.meta,
  }));

  const characterOptions = characters.map((character) => ({
    value: character.username,
    label: pickCharacterLabel(character),
  }));

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/promo-status.php`)
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Gagal memuat status promo.');
        }
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setPromoStatus({
          loading: false,
          activeCount: Number(data.active_count || 0),
          enabled: Boolean(data.promo_enabled),
          message: data.message || '',
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setPromoStatus({
          loading: false,
          activeCount: 0,
          enabled: false,
          message: error.message || 'Saat ini kode promo tidak tersedia.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!returnStatus) return undefined;

    const timer = window.setTimeout(() => {
      setReturnStatus(null);
    }, 10000);

    return () => window.clearTimeout(timer);
  }, [returnStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');

    if (!orderId) return;

    let cancelled = false;
    setReturnStatus({ type: 'info', text: `Mengecek status pembayaran ${orderId}...` });

    fetch(`${API_BASE_URL}/sync-midtrans-status.php?order_id=${encodeURIComponent(orderId)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Gagal sinkron status pembayaran.');
        }
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        const delivered = data.delivery_status === 'delivered';
        const notifText = data.notification_result ? ' Email transaksi sudah diproses.' : '';
        setReturnStatus({
          type: delivered ? 'success' : 'info',
          text: delivered
            ? `Pembayaran ${data.order_id} sukses. Benefit sudah masuk otomatis ke karakter.${notifText}`
            : `Status pembayaran ${data.order_id}: ${data.transaction_status || 'pending'}. Benefit belum dikirim.`,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setReturnStatus({ type: 'error', text: error.message || 'Gagal mengecek status pembayaran.' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (name, value) => {
    setMessage(null);
    if (['categoryId', 'productId', 'levelQuantity', 'promoCode'].includes(name)) {
      setPromoCheck(null);
    }

    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === 'categoryId') { next.productId = ''; next.levelQuantity = '1'; }
      if (name === 'ucpName') {
        next.ucpName = cleanName(value);
        next.characterName = '';
        setCharacters([]);
        setUcpValidated(false);
        setAccountCheck(null);
      }
      if (name === 'characterName') {
        next.characterName = value;
        setAccountCheck(null);
      }
      if (name === 'levelQuantity') {
        const digitsOnly = String(value).replace(/[^0-9]/g, '');
        next.levelQuantity = digitsOnly === '' ? '' : String(Math.max(1, parseInt(digitsOnly, 10) || 1));
      }
      if (name === 'promoCode') {
        next.promoCode = String(value).toUpperCase().replace(/[^A-Z0-9_-]/g, '');
      }

      return next;
    });
  };

  const chooseProduct = (productId) => {
    setField('productId', productId);
  };

  const validate = () => {
    if (!form.categoryId) return 'Pilih kategori top up terlebih dahulu.';
    if (!form.productId) return 'Pilih paket top up terlebih dahulu.';
    if (!form.ucpName.trim()) return 'Nama UCP wajib diisi.';
    if (!ucpValidated) return 'Cek UCP terlebih dahulu agar daftar character bisa dimuat.';
    if (!form.characterName.trim()) return 'Pilih character tujuan dari daftar UCP terlebih dahulu.';
    if (isLevelQuantityProduct && levelQuantity < 1) return 'Jumlah level minimal 1.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Alamat email belum valid.';
    return null;
  };

  const checkAccount = async () => {
    if (!form.ucpName.trim()) {
      setAccountCheck({ type: 'error', text: 'Isi nama UCP terlebih dahulu.' });
      return false;
    }

    setCheckingAccount(true);
    setAccountCheck(null);
    setCharacters([]);
    setUcpValidated(false);

    try {
      const response = await fetch(`${API_BASE_URL}/validate-topup-account.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ucp_name: form.ucpName.trim(),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'UCP tidak valid.');
      }

      const list = Array.isArray(data.characters) ? data.characters : [];
      setCharacters(list);
      setUcpValidated(true);

      setForm((current) => ({
        ...current,
        ucpName: data.account?.username || current.ucpName,
        characterName: list.length === 1 ? list[0].username : '',
        email: current.email || data.account?.email || '',
      }));

      setAccountCheck({
        type: 'success',
        text: list.length > 0
          ? `UCP valid. Ada ${list.length} character yang bisa dipilih untuk top up.`
          : 'UCP valid, tetapi belum ada character aktif yang bisa dipilih.',
        data,
      });
      return true;
    } catch (error) {
      setAccountCheck({ type: 'error', text: error.message || 'Gagal memvalidasi UCP.' });
      return false;
    } finally {
      setCheckingAccount(false);
    }
  };

  const checkPromo = async () => {
    if (!promoFeatureEnabled) {
      setPromoCheck({ type: 'error', text: promoStatus.message || 'Saat ini tidak ada kode promo yang aktif.' });
      return false;
    }

    if (!promoCodeClean) {
      setPromoCheck({ type: 'error', text: 'Isi kode promo terlebih dahulu.' });
      return false;
    }
    if (!form.categoryId || !form.productId || !product) {
      setPromoCheck({ type: 'error', text: 'Pilih kategori dan paket terlebih dahulu.' });
      return false;
    }
    if (!form.ucpName.trim()) {
      setPromoCheck({ type: 'error', text: 'Isi nama UCP terlebih dahulu karena 1 UCP hanya bisa memakai 1x kode promo yang sama.' });
      return false;
    }

    setPromoLoading(true);
    setPromoCheck(null);

    try {
      const response = await fetch(`${API_BASE_URL}/validate-promo.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: form.categoryId,
          product_id: form.productId,
          promo_code: promoCodeClean,
          ucp_name: form.ucpName.trim(),
          level_quantity: isLevelQuantityProduct ? levelQuantity : 1,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Kode promo tidak valid.');
      }

      setPromoCheck({
        type: 'success',
        code: data.promo?.code || promoCodeClean,
        text: `Promo ${data.promo?.code || promoCodeClean} aktif. Diskon ${formatRupiah(data.discount || 0)}.`,
        discount: Number(data.discount || 0),
        baseAmount: Number(data.base_amount || baseTotal || 0),
        grossAmount: Number(data.gross_amount || 0),
        data,
      });
      return true;
    } catch (error) {
      setPromoCheck({ type: 'error', text: error.message || 'Gagal mengecek kode promo.' });
      return false;
    } finally {
      setPromoLoading(false);
    }
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    const error = validate();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/create-midtrans-transaction.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: form.categoryId,
          product_id: form.productId,
          ucp_name: form.ucpName.trim(),
          character_name: form.characterName.trim(),
          asset_detail: form.assetDetail.trim(),
          voucher_code: promoCodeClean,
          email: form.email.trim(),
          level_quantity: isLevelQuantityProduct ? levelQuantity : 1,
          return_from: fromDashboard ? 'dashboard' : '',
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Gagal membuat transaksi Midtrans.');
      }

      setMessage({ type: 'success', text: `Transaksi ${data.order_id} dibuat. Mengalihkan ke halaman pembayaran...` });
      window.setTimeout(() => {
        window.location.href = data.redirect_url;
      }, 650);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Terjadi kesalahan saat membuat pembayaran.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="topup-screen">
      <div className="topup-bg" />

      <header className="topup-header">
        <a className="topup-brand" href={backUrl}>
          <img src={logo} alt="GTA:WestCounty" />
          <span>GTA:WestCounty</span>
        </a>
        <nav className="topup-nav">
          <a href={backUrl}><ArrowLeft size={16} /> {backLabel}</a>
          {!fromDashboard ? <a href="/account/login">Masuk UCP</a> : null}
        </nav>
      </header>

      {returnStatus && <div className={`topup-return-message topup-message ${returnStatus.type}`}>{returnStatus.text}</div>}

      <section className="topup-hero reveal is-visible">
        <div className="topup-title-block">
          <span className="section-label">Donasi Resmi</span>
          <h1>Top up VIP, level, dan gold untuk character GTA:WestCounty.</h1>
          <p>
            Masukkan nama UCP, pilih character yang muncul, lalu selesaikan pembayaran. Benefit akan diproses otomatis setelah transaksi berhasil.
          </p>
        </div>
        <div className="topup-safe-card">
          <div className="topup-safe-badge"><ShieldCheck size={20} /><span>Donasi Aman</span></div>
          <strong>Top Up Cepat & Otomatis</strong>
          <p>Setelah pembayaran berhasil, benefit akan langsung diproses ke karakter tujuan sesuai paket yang kamu pilih.</p>
          <div className="topup-safe-points">
            <div className="topup-safe-point"><span className="point-dot">•</span><span>Pilih UCP lalu tentukan karakter tujuan.</span></div>
            <div className="topup-safe-point"><span className="point-dot">•</span><span>Tersedia paket VIP, Level, dan Gold.</span></div>
            <div className="topup-safe-point"><span className="point-dot">•</span><span>Bukti transaksi dikirim ke email yang kamu isi.</span></div>
          </div>
        </div>
      </section>

      <form className="topup-layout" onSubmit={submitPayment}>
        <section className="topup-form-card">
          <div className="topup-step-head">
            <b>01</b>
            <div>
              <h2>Kategori Top Up</h2>
              <p>Pilih jenis donasi yang ingin kamu proses.</p>
            </div>
          </div>

          <label className="topup-field">
            <span>Pilih Kategori Top Up</span>
            <CustomTopupDropdown
              value={form.categoryId}
              options={categoryOptions}
              placeholder="Pilih kategori"
              onChange={(value) => setField('categoryId', value)}
            />
            {category && <small>{category.hint}</small>}
          </label>

          <div className="topup-step-head compact">
            <b>02</b>
            <div>
              <h2>Pilih Paket</h2>
              <p>Pilih paket yang akan dikirim ke character tujuan.</p>
            </div>
          </div>

          <label className="topup-field">
            <span>Pilih Paket Top Up</span>
            <CustomTopupDropdown
              value={form.productId}
              options={productOptions}
              placeholder={category ? 'Pilih paket' : 'Pilih kategori terlebih dahulu'}
              onChange={(value) => setField('productId', value)}
              disabled={!category}
            />
          </label>

          <div className="topup-products">
            {!category && <div className="topup-empty">Pilih kategori untuk melihat daftar paket.</div>}
            {category && products.map((item) => (
              <button
                key={item.id}
                type="button"
                className={form.productId === item.id ? 'topup-product is-selected' : 'topup-product'}
                onClick={() => chooseProduct(item.id)}
              >
                <span>
                  <b>{item.name}</b>
                  <small>{item.description}</small>
                </span>
                <em>{item.meta}</em>
                <strong>{formatRupiah(item.price)}</strong>
              </button>
            ))}
          </div>

          {isLevelQuantityProduct ? (
            <label className="topup-field topup-level-quantity-field">
              <span>Jumlah Level</span>
              <input
                value={form.levelQuantity}
                onChange={(event) => setField('levelQuantity', event.target.value)}
                placeholder="Masukkan jumlah level"
                inputMode="numeric"
              />
              <small>Harga per 1 level adalah {formatRupiah(product.price)}. Total akan menyesuaikan otomatis.</small>
            </label>
          ) : null}

          <label className="topup-field topup-promo-field">
            <span>Kode Promo</span>
            <div className="topup-promo-row">
              <input
                value={form.promoCode}
                onChange={(event) => setField('promoCode', event.target.value)}
                placeholder={promoFeatureEnabled ? 'Contoh: WEST10' : 'Saat ini tidak ada promo aktif'}
                disabled={!promoFeatureEnabled}
              />
              <button type="button" onClick={checkPromo} disabled={promoLoading || !product || !promoFeatureEnabled}>
                {promoLoading ? 'Cek...' : 'Pakai Promo'}
              </button>
            </div>
            <small>{promoStatus.loading ? 'Sedang memuat status kode promo...' : (promoFeatureEnabled ? `Opsional. Kode promo hanya bisa dipakai 1x untuk setiap UCP. Promo aktif saat ini: ${promoStatus.activeCount}.` : 'Saat ini tidak ada kode promo yang aktif, jadi promo tidak bisa digunakan.')}</small>
          </label>
          {promoCheck && <div className={`topup-message ${promoCheck.type}`}>{promoCheck.text}</div>}

          <div className="topup-step-head compact">
            <b>03</b>
            <div>
              <h2>Validasi UCP</h2>
              <p>Cukup isi UCP, lalu pilih character dari daftar yang muncul.</p>
            </div>
          </div>

          <div className="topup-account-panel">
            <div className="topup-grid-fields topup-grid-fields-account">
              <label className="topup-field">
                <span>Nama UCP</span>
                <input value={form.ucpName} onChange={(event) => setField('ucpName', event.target.value)} placeholder="contoh: bartecz" />
                <small>Masukkan nama UCP kamu, lalu klik tombol cek untuk memuat daftar character.</small>
              </label>
              <label className="topup-field">
                <span>Character Tujuan</span>
                <CustomTopupDropdown
                  value={form.characterName}
                  options={characterOptions}
                  placeholder={ucpValidated ? 'Pilih character tujuan' : 'Cek UCP terlebih dahulu'}
                  onChange={(value) => setField('characterName', value)}
                  disabled={!ucpValidated || characters.length === 0}
                />
                <small>{ucpValidated ? 'Pilih character yang akan menerima benefit top up.' : 'Dropdown character akan aktif setelah UCP berhasil dicek.'}</small>
              </label>
            </div>

            {selectedCharacter ? (
              <div className="topup-character-selected">
                <div>
                  <b>{selectedCharacter.username}</b>
                  <span>{pickCharacterMeta(selectedCharacter)}</span>
                </div>
              </div>
            ) : null}

            <div className="topup-account-actions">
              <button className="topup-check-button" type="button" onClick={checkAccount} disabled={checkingAccount}>
                {checkingAccount ? 'Mengecek UCP...' : 'Cek UCP & Muat Character'} <UserCheck size={17} />
              </button>
            </div>

            {accountCheck && <div className={`topup-message ${accountCheck.type}`}>{accountCheck.text}</div>}
          </div>

          <label className="topup-field">
            <span>Catatan Opsional</span>
            <textarea value={form.assetDetail} onChange={(event) => setField('assetDetail', event.target.value)} placeholder="Boleh dikosongkan. Contoh: catatan untuk staff atau keterangan transaksi." />
          </label>

          <div className="topup-step-head compact">
            <b>04</b>
            <div>
              <h2>Email Transaksi</h2>
              <p>Masukkan email aktif untuk menerima bukti transaksi setelah pembayaran berhasil.</p>
            </div>
          </div>

          <label className="topup-field topup-full-field">
            <span>Alamat Email</span>
            <input value={form.email} onChange={(event) => setField('email', event.target.value)} placeholder="email@domain.com" inputMode="email" />
            <small>Jika UCP memiliki email, kolom ini akan otomatis terisi setelah cek UCP.</small>
          </label>
        </section>

        <aside className="topup-summary-card">
          <div className="summary-logo">
            <img src={logo} alt="GTA:WestCounty" />
          </div>
          <span className="summary-kicker"><WalletCards size={16} /> Ringkasan Top Up</span>
          <h2>{product ? product.name : 'Pilih produk dulu'}</h2>
          <p>{product ? product.description : 'Harga dan detail pembayaran akan muncul setelah kamu memilih kategori serta paket top up.'}</p>

          <div className="summary-lines">
            <div><span>Kategori</span><b>{category?.label || '-'}</b></div>
            <div><span>UCP</span><b>{form.ucpName || '-'}</b></div>
            <div><span>Karakter</span><b>{form.characterName || '-'}</b></div>
            {isLevelQuantityProduct ? <div><span>Jumlah Level</span><b>{levelQuantity} Level</b></div> : null}
            <div><span>{isLevelQuantityProduct ? 'Harga per Level' : 'Harga Produk'}</span><b>{product ? formatRupiah(product.price) : '-'}</b></div>
            {promoCodeClean ? <div><span>Kode Promo</span><b>{promoCodeClean}</b></div> : null}
            {discount > 0 ? <div><span>Diskon</span><b>-{formatRupiah(discount)}</b></div> : null}
            <div className="summary-total"><span>Total Bayar</span><strong>{product ? formatRupiah(total) : '-'}</strong></div>
          </div>

          {message && <div className={`topup-message ${message.type}`}>{message.text}</div>}

          <button className="topup-submit" type="submit" disabled={loading}>
            {loading ? 'Membuat transaksi...' : 'Lanjutkan Pembayaran'} <CreditCard size={18} />
          </button>

          <div className="topup-note-list">
            <div><CheckCircle2 size={17} /><span>Pilih UCP terlebih dahulu agar daftar character bisa dimuat otomatis.</span></div>
            <div><Sparkles size={17} /><span>Tersedia paket VIP, level satuan Rp10.000, paket +3/+5/+10, dan top up gold.</span></div>
            <div><ReceiptText size={17} /><span>Benefit diproses setelah pembayaran dinyatakan berhasil.</span></div>
            <div><MailCheck size={17} /><span>Bukti top up dikirim ke email transaksi yang kamu isi.</span></div>
            <div><BadgeCheck size={17} /><span>Disarankan character sedang offline agar data tidak tertimpa save server.</span></div>
            <div><Headphones size={17} /><span>Simpan order ID jika perlu bantuan pengecekan oleh staff.</span></div>
          </div>
        </aside>
      </form>
    </main>
  );
}
